import React, { useEffect, useState } from 'react'
import { Col, Form, FormInstance, Input, Row } from 'antd'
import { valildateInteger } from 'helper/utility'
import VenueModal from 'pages/Events/Views/CreateEvent/Screens/GeneralDetails/GeneralDetailModal/VenueModal'
import { leagueEventStore } from 'state/league/leagueEvent'
import { OrganizationVenues, OrgVenuesParams } from 'types/organization'
import { LeagueStep4Payload } from 'types/league/league'
import { infoMessage } from 'api/global'
import TimeSlots from './InputComps/TimeSlots'
import { useLeagueRoundRobinTimeSlots } from 'api/league/LeagueCreation/leagueRoundRobin'
import { myStore } from 'state/store'
import { useGetOrgVenues } from 'api/EventCreation/EventCreation'

interface TimeSlotFormProps {
  form: FormInstance
}

const TimeSlotForm: React.FC<TimeSlotFormProps> = ({ form }) => {
  const { leagueData, setLeagueData, timeSlots, setTimeSlotsModal } =
    leagueEventStore(store => ({
      leagueData: store.leagueData,
      setLeagueData: store.setLeagueData,
      timeSlots: store.timeSlots,
      setTimeSlotsModal: store.setTimeSlotsModal,
    }))
  const setModalBtnLoader = myStore(store => store.setModalBtnLoader)

  const [venueModalVisible, setVenueModalVisible] = useState<boolean>(false)
  const [venueDataItem, setVenueDataItem] = useState<OrganizationVenues | null>(
    leagueData?.timeSlots ? leagueData.timeSlots.venue : undefined,
  )

  const formSchema = {
    minsBetweenGames: leagueData.timeSlots
      ? leagueData.timeSlots.minsBetweenGames
      : undefined,
  }

  const setFieldsValue = (leagueData: any) => {
    if (leagueData.timeSlots?.venue) {
      form.setFieldsValue({ location: leagueData.timeSlots.venue.name })
    }
  }

  // The API persists only timeSlots.venueId (no populated venue object), so on
  // reopen we resolve the saved venueId against the org's venues to re-display it.
  const venueLocation: any = leagueData?.location
  const { data: orgVenues, refetch: refetchVenues } = useGetOrgVenues({
    organizationId: leagueData?.organizationId,
    longitude: venueLocation?.coordinates?.longitude,
    latitude: venueLocation?.coordinates?.latitude,
  } as OrgVenuesParams)

  useEffect(() => {
    if (!venueDataItem && leagueData?.timeSlots?.venueId) refetchVenues()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!venueDataItem && leagueData?.timeSlots?.venueId && orgVenues?.length) {
      const matched = orgVenues.find(
        (venue: OrganizationVenues) => venue._id === leagueData.timeSlots.venueId,
      )
      if (matched) setVenueDataItem(matched)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgVenues])

  const { mutate: timeSlotMutation } = useLeagueRoundRobinTimeSlots()

  const onFinish = (values: { minsBetweenGames: number }) => {
    if (!venueDataItem || !venueDataItem._id)
      return infoMessage('Venue is required!')
    const payload: LeagueStep4Payload = {
      timeSlots: {
        ...timeSlots,
        ...values,
        venueId: venueDataItem._id,
      },
    }
    setModalBtnLoader(true)
    timeSlotMutation(
      { leagueId: leagueData._id, payload },
      {
        onSuccess: data => {
          setModalBtnLoader(false)
          setLeagueData({ ...leagueData, ...data })
          setTimeSlotsModal(false)
        },
        onError: () => {
          setModalBtnLoader(false)
        },
      },
    )
  }

  return (
    <>
      <Form
        form={form}
        initialValues={formSchema}
        layout="vertical"
        onFinish={onFinish}
      >
        <div className="overflow-auto-y scroller scroller-lg">
          <TimeSlots />
          <Row gutter={10}>
            <Col className="mt-15px">
              <label className="input-label required-input mb-10px">
                Minutes between games
              </label>

              <Form.Item
                name="minsBetweenGames"
                className="form-control mb-0"
                rules={[
                  {
                    required: true,
                    message: 'Minutes between games is required',
                  },
                  { validator: valildateInteger },
                ]}
              >
                <Input
                  type="number"
                  placeholder="0 Mins"
                  className="text-end radius-12px text-left"
                  onWheel={e => e.currentTarget.blur()}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={10} className="mt-15px">
            <Col>
              <label className="required-input  input-label">Venue</label>
            </Col>
            <Col>
              <div
                className="w-full border-1px radius-10 flex align-center px-12px pointer h-50px"
                onClick={() => setVenueModalVisible(true)}
              >
                {venueDataItem ? (
                  <div className=" w-full flex justify-between align-center">
                    <p className="text-gray">{`${venueDataItem.name}`}</p>
                  </div>
                ) : (
                  <p className="text-gray">Select Venue</p>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </Form>

      {venueModalVisible && (
        <VenueModal
          mapData={leagueData.location}
          setVenueModalVisible={setVenueModalVisible}
          setFieldsValue={setFieldsValue}
          organizationId={leagueData?.organizationId}
          setVenueDataItem={setVenueDataItem}
        />
      )}
    </>
  )
}

export default TimeSlotForm
